// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import BackgroundImageGalleryModal from '@app/features/theme/components/modals/BackgroundImageGalleryModal';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Select} from '@app/features/ui/components/form/Select';
import {Spinner} from '@app/features/ui/components/Spinner';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import * as VoiceSettingsCommands from '@app/features/voice/commands/VoiceSettingsCommands';
import styles from '@app/features/voice/components/modals/CameraPreviewModal.module.css';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import VoiceDevicePermissionState from '@app/features/voice/engine/VoiceDevicePermissionState';
import VoiceMediaStateCoordinator from '@app/features/voice/engine/VoiceMediaStateCoordinator';
import VoiceSettings, {BLUR_BACKGROUND_ID, NONE_BACKGROUND_ID} from '@app/features/voice/state/VoiceSettings';
import {applyBackgroundProcessor} from '@app/features/voice/utils/VideoBackgroundProcessor';
import type {VoiceDeviceState} from '@app/features/voice/utils/VoiceDeviceManager';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {useLocalParticipant} from '@livekit/components-react';
import {CameraIcon, ImageIcon} from '@phosphor-icons/react';
import type {LocalParticipant, LocalVideoTrack} from 'livekit-client';
import {createLocalVideoTrack} from 'livekit-client';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const logger = new Logger('CameraPreviewModal');
interface CameraPreviewModalProps {
	onEnabled?: () => void;
	onEnableCamera?: () => void;
	showEnableCameraButton?: boolean;
	localParticipant?: LocalParticipant;
	isCameraEnabled?: boolean;
}
interface VideoResolutionPreset {
	width: number;
	height: number;
	frameRate: number;
}

const TARGET_ASPECT_RATIO = 16 / 9;
const ASPECT_RATIO_TOLERANCE = 0.1;
const RESOLUTION_WAIT_TIMEOUT = 2000;
const RESOLUTION_CHECK_INTERVAL = 100;
const VIDEO_ELEMENT_WAIT_TIMEOUT = 5000;
const VIDEO_ELEMENT_CHECK_INTERVAL = 10;
const CAMERA_RESOLUTION_PRESETS: Record<'low' | 'medium' | 'high', VideoResolutionPreset> = {
	low: {width: 640, height: 360, frameRate: 24},
	medium: {width: 1280, height: 720, frameRate: 30},
	high: {width: 1920, height: 1080, frameRate: 30},
};
const CameraPreviewModalContent = observer((props: CameraPreviewModalProps) => {
	const {t} = useLingui();
	const {localParticipant, onEnabled, onEnableCamera, isCameraEnabled, showEnableCameraButton = true} = props;
	const channelId = MediaEngine.channelId;
	const guildId = MediaEngine.guildId;
	const canStream = !localParticipant || !guildId || !channelId || Permission.can(Permissions.STREAM, {channelId});
	const [videoDevices, setVideoDevices] = useState<Array<MediaDeviceInfo>>([]);
	const [status, setStatus] = useState<
		'idle' | 'initializing' | 'ready' | 'error' | 'fixing' | 'fix-settling' | 'fix-switching-back'
	>('initializing');
	const [error, setError] = useState<string | null>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const trackRef = useRef<LocalVideoTrack | null>(null);
	const processorRef = useRef<{destroy: () => Promise<void>} | null>(null);
	const isMountedRef = useRef(true);
	const isIOSRef = useRef(/iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window));
	const prevConfigRef = useRef<{
		videoDeviceId: string;
		backgroundImageId: string;
		cameraResolution: 'low' | 'medium' | 'high';
		videoFrameRate: number;
	} | null>(null);
	const originalBackgroundIdRef = useRef<string | null>(VoiceSettings.backgroundImageId);
	const needsResolutionFixRef = useRef(false);
	const isApplyingFixRef = useRef(false);
	const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const fixTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const settleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const switchBackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const handleDeviceUpdate = useCallback((state: VoiceDeviceState) => {
		if (!isMountedRef.current) return;
		const videoInputs = state.videoDevices.filter((device) => device.deviceId !== 'default');
		setVideoDevices(videoInputs);
		const voiceSettings = VoiceSettings;
		const currentDeviceId = voiceSettings.videoDeviceId;
		const currentDeviceExists = videoInputs.some((device) => device.deviceId === currentDeviceId);
		if (videoInputs.length > 0 && (currentDeviceId === 'default' || !currentDeviceExists)) {
			VoiceSettingsCommands.update({videoDeviceId: videoInputs[0].deviceId});
		}
	}, []);
	const applyResolutionFix = useCallback(() => {
		if (!isMountedRef.current || isApplyingFixRef.current) {
			return;
		}
		isApplyingFixRef.current = true;
		needsResolutionFixRef.current = false;
		const voiceSettings = VoiceSettings;
		const currentBg = voiceSettings.backgroundImageId;
		const tempBg = currentBg === NONE_BACKGROUND_ID ? BLUR_BACKGROUND_ID : NONE_BACKGROUND_ID;
		setStatus('fixing');
		VoiceSettingsCommands.update({backgroundImageId: tempBg});
		settleTimeoutRef.current = setTimeout(() => {
			setStatus('fix-switching-back');
			VoiceSettingsCommands.update({backgroundImageId: originalBackgroundIdRef.current!});
			switchBackTimeoutRef.current = setTimeout(() => {
				if (isMountedRef.current) {
					isApplyingFixRef.current = false;
					setStatus('ready');
				}
			}, 500);
		}, 1200);
	}, []);
	const initializeCamera = useCallback(async () => {
		const voiceSettings = VoiceSettings;
		const isMobile = MobileLayout.isMobileLayout() || isIOSRef.current;
		if (isMobile) {
			if (isMountedRef.current) {
				setStatus('ready');
			}
			return;
		}
		if (!isMountedRef.current) {
			return;
		}
		let videoElement = videoRef.current;
		let attempts = 0;
		const maxAttempts = VIDEO_ELEMENT_WAIT_TIMEOUT / VIDEO_ELEMENT_CHECK_INTERVAL;
		while (!videoElement && attempts < maxAttempts) {
			await new Promise((resolve) => setTimeout(resolve, VIDEO_ELEMENT_CHECK_INTERVAL));
			videoElement = videoRef.current;
			attempts++;
		}
		if (!videoElement) {
			if (isMountedRef.current) {
				setStatus('error');
				setError('Video element not available');
			}
			return;
		}
		try {
			const currentConfig = {
				videoDeviceId: voiceSettings.videoDeviceId,
				backgroundImageId: voiceSettings.backgroundImageId,
				cameraResolution: voiceSettings.cameraResolution,
				videoFrameRate: voiceSettings.videoFrameRate,
			};
			if (prevConfigRef.current && JSON.stringify(prevConfigRef.current) === JSON.stringify(currentConfig)) {
				return;
			}
			prevConfigRef.current = currentConfig;
			if (!originalBackgroundIdRef.current) {
				originalBackgroundIdRef.current = voiceSettings.backgroundImageId;
			}
			if (isMountedRef.current) {
				setStatus(isApplyingFixRef.current ? 'fixing' : 'initializing');
				setError(null);
			}
			videoElement.muted = true;
			videoElement.autoplay = true;
			videoElement.playsInline = true;
			if (trackRef.current) {
				trackRef.current.stop();
				trackRef.current = null;
			}
			if (processorRef.current) {
				await processorRef.current.destroy();
				processorRef.current = null;
			}
			const resolutionPreset = CAMERA_RESOLUTION_PRESETS[voiceSettings.cameraResolution];
			const track = await createLocalVideoTrack({
				deviceId:
					voiceSettings.videoDeviceId && voiceSettings.videoDeviceId !== 'default'
						? voiceSettings.videoDeviceId
						: undefined,
				resolution: {
					width: resolutionPreset.width,
					height: resolutionPreset.height,
					frameRate: voiceSettings.videoFrameRate,
					aspectRatio: TARGET_ASPECT_RATIO,
				},
			});
			if (!isMountedRef.current) {
				track.stop();
				return;
			}
			trackRef.current = track;
			track.attach(videoElement);
			const actualDeviceId = track.mediaStreamTrack.getSettings().deviceId;
			if (actualDeviceId && actualDeviceId !== voiceSettings.videoDeviceId) {
				VoiceSettingsCommands.update({videoDeviceId: actualDeviceId});
			}
			await new Promise<void>((resolve) => {
				let playbackAttempts = 0;
				const checkPlayback = () => {
					const hasData = videoElement!.srcObject && videoElement!.readyState >= 2;
					if (hasData) {
						resolve();
					} else if (++playbackAttempts < 100) {
						setTimeout(checkPlayback, 50);
					} else {
						resolve();
					}
				};
				checkPlayback();
			});
			if (!isMountedRef.current) {
				track.stop();
				return;
			}
			let negotiatedResolution: {width: number; height: number} | null = null;
			await new Promise<void>((resolve) => {
				let resolutionAttempts = 0;
				const checkResolution = () => {
					const settings = track.mediaStreamTrack.getSettings();
					if (settings.width && settings.height) {
						negotiatedResolution = {width: settings.width, height: settings.height};
						resolve();
					} else if (++resolutionAttempts < RESOLUTION_WAIT_TIMEOUT / RESOLUTION_CHECK_INTERVAL) {
						setTimeout(checkResolution, RESOLUTION_CHECK_INTERVAL);
					} else {
						resolve();
					}
				};
				checkResolution();
			});
			if (!isMountedRef.current) {
				track.stop();
				return;
			}
			if (negotiatedResolution && !isApplyingFixRef.current) {
				const {width, height} = negotiatedResolution;
				const aspectRatio = width / height;
				const isValid16x9 = Math.abs(aspectRatio - TARGET_ASPECT_RATIO) < ASPECT_RATIO_TOLERANCE;
				needsResolutionFixRef.current = !isValid16x9;
			}
			try {
				processorRef.current = await applyBackgroundProcessor(track);
			} catch (_webglError) {
				logger.warn('WebGL not supported for background processing, falling back to basic camera');
			}
			if (!isMountedRef.current) {
				track.stop();
				return;
			}
			if (isMountedRef.current) {
				setStatus('ready');
				if (needsResolutionFixRef.current && !isApplyingFixRef.current) {
					initializationTimeoutRef.current = setTimeout(() => applyResolutionFix(), 800);
				}
			}
		} catch (err) {
			if (isMountedRef.current) {
				const message = err instanceof Error ? err.message : 'Unknown error';
				setStatus('error');
				setError(message);
				ToastCommands.createToast({
					type: 'error',
					children: t`Failed to start camera preview. Please check your camera permissions.`,
				});
			}
		}
	}, [applyResolutionFix]);
	const handleDeviceChange = useCallback((deviceId: string) => {
		VoiceSettingsCommands.update({videoDeviceId: deviceId});
	}, []);
	const handleOpenBackgroundGallery = useCallback(() => {
		ModalCommands.push(modal(() => <BackgroundImageGalleryModal />));
	}, []);
	const handleEnableCamera = useCallback(async () => {
		if (!localParticipant) {
			onEnabled?.();
			onEnableCamera?.();
			ModalCommands.pop();
			return;
		}
		try {
			const voiceSettings = VoiceSettings;
			await localParticipant.setCameraEnabled(true, {
				deviceId: voiceSettings.videoDeviceId !== 'default' ? voiceSettings.videoDeviceId : undefined,
			});
			VoiceMediaStateCoordinator.applyCameraState(true, {reason: 'user', sendUpdate: true});
			onEnabled?.();
			onEnableCamera?.();
			ModalCommands.pop();
		} catch (_err) {
			ToastCommands.createToast({
				type: 'error',
				children: t`Failed to enable camera.`,
			});
		}
	}, [localParticipant, onEnabled, onEnableCamera]);
	useEffect(() => {
		isMountedRef.current = true;
		const unsubscribeDevices = VoiceDevicePermissionState.subscribe(handleDeviceUpdate);
		void VoiceDevicePermissionState.ensureDevices({requestPermissions: true}).catch(() => {});
		initializeCamera();
		return () => {
			isMountedRef.current = false;
			if (initializationTimeoutRef.current) clearTimeout(initializationTimeoutRef.current);
			if (fixTimeoutRef.current) clearTimeout(fixTimeoutRef.current);
			if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
			if (switchBackTimeoutRef.current) clearTimeout(switchBackTimeoutRef.current);
			if (trackRef.current) {
				trackRef.current.stop();
				trackRef.current = null;
			}
			if (processorRef.current) {
				processorRef.current.destroy().catch(() => {});
				processorRef.current = null;
			}
			if (videoRef.current) {
				try {
					if (videoRef.current.srcObject) {
						videoRef.current.srcObject = null;
					}
				} catch {}
			}
			unsubscribeDevices?.();
		};
	}, [handleDeviceUpdate, initializeCamera]);
	useEffect(() => {
		const voiceSettings = VoiceSettings;
		const currentConfig = {
			videoDeviceId: voiceSettings.videoDeviceId,
			backgroundImageId: voiceSettings.backgroundImageId,
			cameraResolution: voiceSettings.cameraResolution,
			videoFrameRate: voiceSettings.videoFrameRate,
		};
		const configChanged =
			!prevConfigRef.current || JSON.stringify(prevConfigRef.current) !== JSON.stringify(currentConfig);
		if (configChanged) {
			initializeCamera();
		}
	}, [
		initializeCamera,
		VoiceSettings.videoDeviceId,
		VoiceSettings.backgroundImageId,
		VoiceSettings.cameraResolution,
		VoiceSettings.videoFrameRate,
	]);
	const voiceSettings = VoiceSettings;
	const videoDeviceOptions = videoDevices.map((device) => ({
		value: device.deviceId,
		label: device.label || t`Camera ${device.deviceId.slice(0, 8)}`,
	}));
	return (
		<Modal.Root size="medium">
			<Modal.Header title={t`Camera Preview`} />
			<Modal.Content>
				<div className={styles.content}>
					<div>
						<Select
							label={t`Camera`}
							value={voiceSettings.videoDeviceId}
							options={videoDeviceOptions}
							onChange={handleDeviceChange}
						/>
					</div>
					<div className={styles.backgroundSection}>
						<div className={styles.backgroundLabel}>
							<Trans>Background</Trans>
						</div>
						<Button variant="primary" onClick={handleOpenBackgroundGallery} leftIcon={<ImageIcon size={16} />}>
							<Trans>Change Background</Trans>
						</Button>
					</div>
					<div className={styles.videoContainer}>
						<video ref={videoRef} autoPlay playsInline muted className={styles.video} aria-label={t`Camera Preview`} />
						{(status === 'initializing' || status === 'fixing' || status === 'fix-switching-back') && (
							<div className={styles.overlay}>
								<Spinner />
								<div className={styles.overlayText}>
									<div className={styles.overlayTextMedium}>
										{status === 'fixing' ? (
											<Trans>Optimizing camera...</Trans>
										) : status === 'fix-switching-back' ? (
											<Trans>Finalizing camera...</Trans>
										) : (
											<Trans>Initializing camera...</Trans>
										)}
									</div>
								</div>
							</div>
						)}
						{status === 'error' && (
							<div className={styles.errorOverlay}>
								<div className={styles.errorText}>
									<div className={styles.errorTitle}>
										<Trans>Camera error</Trans>
									</div>
									<div className={styles.errorDetail}>{error}</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</Modal.Content>
			<Modal.Footer>
				<Button variant="secondary" onClick={() => ModalCommands.pop()}>
					<Trans>Cancel</Trans>
				</Button>
				{showEnableCameraButton &&
					!isCameraEnabled &&
					(canStream ? (
						<Button onClick={handleEnableCamera} leftIcon={<CameraIcon size={16} />}>
							<Trans>Turn On Camera</Trans>
						</Button>
					) : (
						<Tooltip text={t`You don't have permission to turn on your camera in this channel`}>
							<Button onClick={undefined} leftIcon={<CameraIcon size={16} />} disabled>
								<Trans>Turn On Camera</Trans>
							</Button>
						</Tooltip>
					))}
			</Modal.Footer>
		</Modal.Root>
	);
});
export const CameraPreviewModalInRoom: React.FC<Omit<CameraPreviewModalProps, 'localParticipant' | 'isCameraEnabled'>> =
	observer((props) => {
		const {localParticipant, isCameraEnabled} = useLocalParticipant();
		return (
			<CameraPreviewModalContent localParticipant={localParticipant} isCameraEnabled={isCameraEnabled} {...props} />
		);
	});
export const CameraPreviewModalStandalone: React.FC<CameraPreviewModalProps> = observer((props) => {
	return <CameraPreviewModalContent localParticipant={undefined} isCameraEnabled={false} {...props} />;
});
